import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldAlert, Users, Package, ShoppingBag, TrendingUp, LogOut, Search,
  ShieldCheck, ShieldOff, Ban, CheckCircle2, Trash2, Star, StarOff,
  Wallet, ClipboardCheck, ClipboardX, Clock3, UserCheck, UserX, FileText,
  Download, ImageOff, Tag, BadgeCheck, MapPin, Eye, Boxes, ExternalLink, Truck,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { formatINR } from '@/lib/format';
import { adminApi } from '@/lib/api';
import { getStoredUser, getStoredToken, clearAuth } from '@/lib/authStore';
import { Toaster, toast } from 'sonner';

// Documents are stored as base64 data URIs (see auth.controller.js). Derive
// a sensible file extension from the embedded mime type so downloads don't
// just save as an extensionless blob.
function extensionForDataUri(dataUri) {
  const match = /^data:([^;]+);/.exec(dataUri || '');
  const mime = match?.[1] || '';
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('png')) return 'png';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  return 'bin';
}

function downloadFileNameFor(sellerName, label, dataUri) {
  const slug = `${sellerName || 'seller'}-${label}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  return `${slug}.${extensionForDataUri(dataUri)}`;
}

function AdminKpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="stat-card p-4" style={{ borderTopColor: accent || 'var(--primary)', borderTopWidth: '3px' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 border" style={{ borderColor: accent || 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.06)' }}>
          <Icon size={15} style={{ color: accent || 'var(--primary)' }} />
        </div>
      </div>
      <p className="text-xl font-heading font-mono-data" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </p>
      <p className="text-xs font-heading tracking-stamp mt-1" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
        {label}
      </p>
    </div>
  );
}

function Toolbar({ search, onSearch, children }) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div className="relative flex-1 min-w-[200px]">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search..."
          className="input-ledger w-full pl-9 pr-3 py-2 text-sm"
        />
      </div>
      {children}
    </div>
  );
}

// ─── Users Tab ──────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('pending'); // default to the actionable queue
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [kycTarget, setKycTarget] = useState(null); // user row being reviewed
  const [kycDetail, setKycDetail] = useState(null); // full detail incl. documents
  const [kycLoading, setKycLoading] = useState(false);

  const openKycReview = async (user) => {
    setKycTarget(user);
    setKycDetail(null);
    setKycLoading(true);
    try {
      const data = await adminApi.userDetail(user.id);
      setKycDetail(data.user);
    } catch (err) {
      toast.error(err.message);
      setKycTarget(null);
    } finally {
      setKycLoading(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    adminApi
      .users({ search, role: roleFilter, approvalStatus: approvalFilter })
      .then((data) => setUsers(data.users || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [search, roleFilter, approvalFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleApproval = async (user, approvalStatus) => {
    try {
      await adminApi.setUserApproval(user.id, approvalStatus);
      toast.success(`${user.name} ${approvalStatus === 'approved' ? 'approved — they can now log in.' : 'rejected.'}`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSuspend = async (user) => {
    try {
      await adminApi.suspendUser(user.id, !user.isSuspended);
      toast.success(user.isSuspended ? `${user.name} reactivated.` : `${user.name} suspended.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleVerify = async (user) => {
    try {
      await adminApi.verifyUser(user.id, !user.isVerified);
      toast.success(user.isVerified ? `Verification removed for ${user.name}.` : `${user.name} marked as verified.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteUser(deleteTarget.id);
      toast.success(`${deleteTarget.name} deleted.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.message);
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <Toolbar search={search} onSearch={setSearch}>
        <div className="flex gap-2">
          {['', 'seller', 'buyer'].map((r) => (
            <button
              key={`role-filter-${r || 'all'}`}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-2 text-xs ${roleFilter === r ? 'tab-active' : 'tab-inactive'}`}
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {r ? r.toUpperCase() + 'S' : 'ALL'}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {[
            { key: 'pending', label: 'AWAITING APPROVAL' },
            { key: 'approved', label: 'APPROVED' },
            { key: 'rejected', label: 'REJECTED' },
            { key: '', label: 'ALL' },
          ].map((f) => (
            <button
              key={`approval-filter-${f.key || 'all'}`}
              onClick={() => setApprovalFilter(f.key)}
              className={`px-3 py-2 text-xs whitespace-nowrap ${approvalFilter === f.key ? 'tab-active' : 'tab-inactive'}`}
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Toolbar>

      {loading ? (
        <LoadingBlock label="LOADING USERS..." />
      ) : users.length === 0 ? (
        <EmptyBlock
          icon={Users}
          message={approvalFilter === 'pending' ? 'No accounts are waiting for approval right now.' : 'No sellers or buyers match your filters.'}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--foreground)' }}>
                {['NAME / EMAIL', 'ROLE', 'COMPANY / CITY', 'REVENUE (SELLERS)', 'VERIFICATION', 'APPROVAL', 'STATUS', 'ACTIONS'].map((c) => (
                  <th key={c} className="px-3 py-2.5 text-left text-xs font-heading tracking-stamp whitespace-nowrap" style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className="border-b border-border" style={{ backgroundColor: i % 2 === 0 ? 'var(--card)' : 'transparent' }}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm">{u.name}</p>
                      {u.isVerified && <ShieldCheck size={12} style={{ color: 'var(--secondary)' }} title="Verified" />}
                    </div>
                    <p className="text-xs font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>{u.email}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs font-heading tracking-stamp px-2 py-0.5 border" style={{ borderColor: u.role === 'seller' ? 'var(--primary)' : 'var(--secondary)', color: u.role === 'seller' ? 'var(--primary)' : 'var(--secondary)', fontFamily: 'var(--font-heading)' }}>
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <p>{u.company || '—'}</p>
                    <p style={{ color: 'var(--muted-foreground)' }}>{u.city}</p>
                  </td>
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                    {u.role === 'seller' ? (
                      <>
                        <p className="font-mono-data font-medium" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                          {formatINR(u.revenueDelivered || 0)}
                        </p>
                        {u.revenuePending > 0 && (
                          <p style={{ color: 'var(--amber)' }}>
                            +{formatINR(u.revenuePending)} in transit
                          </p>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <p style={{ color: u.emailVerified ? 'var(--secondary)' : 'var(--muted-foreground)' }}>
                      {u.emailVerified ? '✓ Email verified' : '✗ Email verified'}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        borderColor: u.approvalStatus === 'approved' ? 'var(--secondary)' : u.approvalStatus === 'rejected' ? 'var(--danger)' : 'var(--amber)',
                        color: u.approvalStatus === 'approved' ? 'var(--secondary)' : u.approvalStatus === 'rejected' ? 'var(--danger)' : 'var(--amber)',
                        backgroundColor: u.approvalStatus === 'approved' ? 'var(--success-bg)' : u.approvalStatus === 'rejected' ? 'var(--danger-bg)' : 'var(--amber-bg)',
                      }}
                    >
                      {u.approvalStatus?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`${u.isSuspended ? 'status-inactive' : 'status-active'} px-2 py-0.5 text-xs font-heading tracking-stamp inline-block`} style={{ fontFamily: 'var(--font-heading)' }}>
                      {u.isSuspended ? 'SUSPENDED' : 'ACTIVE'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {u.approvalStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproval(u, 'approved')}
                            disabled={!u.emailVerified}
                            title={!u.emailVerified ? 'Awaiting email verification' : 'Approve account'}
                            className="p-1.5 border hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ borderColor: 'var(--secondary)' }}
                          >
                            <UserCheck size={13} style={{ color: 'var(--secondary)' }} />
                          </button>
                          <button onClick={() => handleApproval(u, 'rejected')} title="Reject account" className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--danger)' }}>
                            <UserX size={13} style={{ color: 'var(--danger)' }} />
                          </button>
                        </>
                      )}
                      {u.role === 'seller' && (
                        <button onClick={() => openKycReview(u)} title="Review KYC (GSTIN/PAN/bank + documents)" className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--primary)' }}>
                          <FileText size={13} style={{ color: 'var(--primary)' }} />
                        </button>
                      )}
                      {u.role === 'seller' && (
                        <button onClick={() => handleVerify(u)} title={u.isVerified ? 'Remove verification' : 'Verify seller'} className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--secondary)' }}>
                          {u.isVerified ? <ShieldOff size={13} style={{ color: 'var(--secondary)' }} /> : <ShieldCheck size={13} style={{ color: 'var(--secondary)' }} />}
                        </button>
                      )}
                      <button onClick={() => handleSuspend(u)} title={u.isSuspended ? 'Reactivate' : 'Suspend'} className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--amber)' }}>
                        {u.isSuspended ? <CheckCircle2 size={13} style={{ color: 'var(--amber)' }} /> : <Ban size={13} style={{ color: 'var(--amber)' }} />}
                      </button>
                      <button onClick={() => setDeleteTarget(u)} title="Delete account" className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--danger)' }}>
                        <Trash2 size={13} style={{ color: 'var(--danger)' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete account"
        message={`Permanently delete ${deleteTarget?.name}? This removes their listings and order history too. This cannot be undone.`}
        confirmLabel="DELETE"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />

      {kycTarget && (
        <KycReviewModal
          user={kycTarget}
          detail={kycDetail}
          loading={kycLoading}
          onClose={() => { setKycTarget(null); setKycDetail(null); }}
          onApprove={async () => {
            await handleApproval(kycTarget, 'approved');
            setKycTarget(null);
            setKycDetail(null);
          }}
          onReject={async () => {
            await handleApproval(kycTarget, 'rejected');
            setKycTarget(null);
            setKycDetail(null);
          }}
        />
      )}
    </div>
  );
}

// KYC review modal — shows a seller's GSTIN/PAN/bank details plus the three
// uploaded documents (opened in a new tab) so an admin can verify everything
// in one place before approving or rejecting the account.
function KycReviewModal({ user, detail, loading, onClose, onApprove, onReject }) {
  const [deciding, setDeciding] = useState('');

  const handleDecision = async (action, fn) => {
    setDeciding(action);
    try {
      await fn();
    } finally {
      setDeciding('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div
        className="w-full max-w-lg mx-4 paper-texture border-2 border-foreground ink-shadow max-h-[90vh] overflow-y-auto"
        style={{ animation: 'fadeIn 150ms ease' }}
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-foreground sticky top-0 z-10" style={{ backgroundColor: 'var(--card)' }}>
          <div className="flex items-center gap-2">
            <FileText size={16} style={{ color: 'var(--primary)' }} />
            <h3 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              SELLER KYC REVIEW
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:opacity-70 transition-opacity">
            <UserX size={16} />
          </button>
        </div>

        {loading || !detail ? (
          <div className="p-8 flex justify-center">
            <div className="inline-block w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)' }} />
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-4">
            <div>
              <p className="text-sm font-medium">{detail.name}</p>
              <p className="text-xs font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>{detail.email}</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{detail.company} · {detail.city}</p>
            </div>

            {detail.revenueSummary && (
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                  <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>REVENUE (DELIVERED)</p>
                  <p className="text-sm font-heading" style={{ fontFamily: 'var(--font-mono)', color: 'var(--secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatINR(detail.revenueSummary.revenueDelivered)}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{detail.revenueSummary.ordersDelivered} order(s)</p>
                </div>
                <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                  <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>IN FULFILLMENT</p>
                  <p className="text-sm font-heading" style={{ fontFamily: 'var(--font-mono)', color: 'var(--amber)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatINR(detail.revenueSummary.revenuePending)}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{detail.revenueSummary.ordersInPipeline} order(s) to follow up</p>
                </div>
                <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                  <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>COMMISSION EARNED</p>
                  <p className="text-sm font-heading" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatINR(detail.revenueSummary.platformCommissionEarned)}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>from this seller</p>
                </div>
              </div>
            )}

            <div className="border" style={{ borderColor: 'var(--border)' }}>
              {[
                ['GSTIN', detail.gstin],
                ['PAN', detail.pan],
                ['Bank Account Holder', detail.bankAccountHolder],
                ['Bank Account Number', detail.bankAccountNumber],
                ['Bank IFSC', detail.bankIfsc],
              ].map(([label, value], i) => (
                <div key={label} className="grid grid-cols-2 text-sm" style={{ backgroundColor: i % 2 === 0 ? 'var(--card)' : 'transparent', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                  <div className="px-3 py-2 font-medium" style={{ borderRight: '1px solid var(--border)' }}>{label}</div>
                  <div className="px-3 py-2 font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: value ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                    {value || 'Not provided'}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <p className="text-xs font-heading tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                UPLOADED DOCUMENTS
              </p>
              <div className="flex flex-col gap-2">
                {[
                  ['gstin-certificate', 'GSTIN Certificate', detail.gstinDocumentUrl],
                  ['pan-card', 'PAN Card', detail.panDocumentUrl],
                  ['bank-passbook', 'Bank Passbook / Cancelled Cheque', detail.bankPassbookDocumentUrl],
                ].map(([slug, label, url]) => (
                  <div key={label} className="flex items-center justify-between px-3 py-2 border" style={{ borderColor: 'var(--border)' }}>
                    <span className="text-sm">{label}</span>
                    {url ? (
                      <div className="flex items-center gap-1.5">
                        <a href={url} target="_blank" rel="noreferrer" title="Open in a new tab" className="btn-secondary px-2.5 py-1 text-xs flex items-center gap-1">
                          <ExternalLink size={11} /> VIEW
                        </a>
                        <a
                          href={url}
                          download={downloadFileNameFor(detail.company || detail.name, slug, url)}
                          title="Download to your computer"
                          className="btn-secondary px-2.5 py-1 text-xs flex items-center gap-1"
                        >
                          <Download size={11} /> DOWNLOAD
                        </a>
                      </div>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--danger)' }}>Missing</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {detail.approvalStatus === 'pending' && (
              <div className="flex gap-3 pt-2 border-t border-border">
                <button
                  onClick={() => handleDecision('rejected', onReject)}
                  disabled={!!deciding}
                  className="btn-danger flex-1 px-4 py-2.5 text-sm disabled:opacity-60"
                >
                  {deciding === 'rejected' ? 'REJECTING...' : 'REJECT'}
                </button>
                <button
                  onClick={() => handleDecision('approved', onApprove)}
                  disabled={!!deciding || !detail.emailVerified}
                  className="btn-primary flex-1 px-4 py-2.5 text-sm disabled:opacity-60"
                  title={!detail.emailVerified ? 'Awaiting email verification' : undefined}
                >
                  {deciding === 'approved' ? 'APPROVING...' : 'APPROVE'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Listings Tab ───────────────────────────────────────────
function ListingsTab() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null); // listing being viewed in full, buyer-style
  const [kycTarget, setKycTarget] = useState(null); // seller being reviewed from within the listing modal
  const [kycDetail, setKycDetail] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);

  const openKycFromListing = async (listing) => {
    setKycTarget({ id: listing.sellerId, name: listing.sellerCompany });
    setKycDetail(null);
    setKycLoading(true);
    try {
      const data = await adminApi.userDetail(listing.sellerId);
      setKycDetail(data.user);
    } catch (err) {
      toast.error(err.message);
      setKycTarget(null);
    } finally {
      setKycLoading(false);
    }
  };

  const handleKycApproval = async (approvalStatus) => {
    if (!kycTarget) return;
    try {
      await adminApi.setUserApproval(kycTarget.id, approvalStatus);
      toast.success(`${kycTarget.name} ${approvalStatus === 'approved' ? 'approved.' : 'rejected.'}`);
      setKycTarget(null);
      setKycDetail(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    adminApi
      .listings({ search, adminStatus: statusFilter })
      .then((data) => setListings(data.listings || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleModerate = async (listing, adminStatus) => {
    if (adminStatus === 'rejected') {
      setRejectTarget(listing);
      setRejectReason('');
      return;
    }
    try {
      await adminApi.moderateListing(listing.id, adminStatus);
      toast.success(`"${listing.title.slice(0, 30)}..." marked ${adminStatus}.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please enter a reason for rejection.');
      return;
    }
    setRejecting(true);
    try {
      await adminApi.moderateListing(rejectTarget.id, 'rejected', rejectReason.trim());
      toast.success(`"${rejectTarget.title.slice(0, 30)}..." rejected — the seller will see your remarks.`);
      setRejectTarget(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRejecting(false);
    }
  };

  const handleFeature = async (listing) => {
    try {
      await adminApi.featureListing(listing.id, !listing.isFeatured);
      toast.success(listing.isFeatured ? 'Removed featured placement.' : 'Listing featured on marketplace.');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteListing(deleteTarget.id);
      toast.success('Listing deleted.');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.message);
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <Toolbar search={search} onSearch={setSearch}>
        <div className="flex gap-2">
          {['', 'approved', 'pending', 'rejected'].map((s) => (
            <button
              key={`status-filter-${s || 'all'}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-xs ${statusFilter === s ? 'tab-active' : 'tab-inactive'}`}
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {s ? s.toUpperCase() : 'ALL'}
            </button>
          ))}
        </div>
      </Toolbar>

      {loading ? (
        <LoadingBlock label="LOADING LISTINGS..." />
      ) : listings.length === 0 ? (
        <EmptyBlock icon={Package} message="No listings match your filters." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--foreground)' }}>
                {['LISTING', 'SELLER', 'PRICE / QTY', 'MODERATION', 'FEATURED', 'ACTIONS'].map((c) => (
                  <th key={c} className="px-3 py-2.5 text-left text-xs font-heading tracking-stamp whitespace-nowrap" style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listings.map((l, i) => (
                <tr key={l.id} className="border-b border-border" style={{ backgroundColor: i % 2 === 0 ? 'var(--card)' : 'transparent' }}>
                  <td className="px-3 py-2.5 max-w-[280px]">
                    <button onClick={() => setDetailTarget(l)} className="flex items-center gap-2 text-left w-full group" title="Open full listing review, as a buyer would see it">
                      <div
                        className="flex-shrink-0 border overflow-hidden flex items-center justify-center"
                        style={{ width: '36px', height: '36px', borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
                      >
                        {l.imageUrl ? (
                          <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageOff size={13} style={{ color: 'var(--muted-foreground)' }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate group-hover:underline">{l.title}</p>
                        <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                          {l.category}
                          {l.sellerNotes && <span style={{ color: 'var(--primary)' }}> · has a note to admin</span>}
                        </p>
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <p>{l.sellerCompany}</p>
                    <p style={{ color: 'var(--muted-foreground)' }}>{l.sellerCity}</p>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>
                    {formatINR(l.price)} · {l.quantity.toLocaleString('en-IN')} units
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      {['approved', 'pending', 'rejected'].map((s) => (
                        <button
                          key={s}
                          onClick={() => handleModerate(l, s)}
                          title={`Mark ${s}`}
                          className={`px-1.5 py-1 border text-xs`}
                          style={{
                            borderColor: l.adminStatus === s
                              ? (s === 'approved' ? 'var(--secondary)' : s === 'rejected' ? 'var(--danger)' : 'var(--amber)')
                              : 'var(--border)',
                            backgroundColor: l.adminStatus === s
                              ? (s === 'approved' ? 'var(--success-bg)' : s === 'rejected' ? 'var(--danger-bg)' : 'var(--amber-bg)')
                              : 'transparent',
                            color: l.adminStatus === s
                              ? (s === 'approved' ? 'var(--secondary)' : s === 'rejected' ? 'var(--danger)' : 'var(--amber)')
                              : 'var(--muted-foreground)',
                          }}
                        >
                          {s === 'approved' ? <ClipboardCheck size={12} /> : s === 'rejected' ? <ClipboardX size={12} /> : <Clock3 size={12} />}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => handleFeature(l)} className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--amber)' }} title={l.isFeatured ? 'Remove featured placement' : 'Feature this listing'}>
                      {l.isFeatured ? <Star size={13} fill="var(--amber)" style={{ color: 'var(--amber)' }} /> : <StarOff size={13} style={{ color: 'var(--amber)' }} />}
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => setDeleteTarget(l)} title="Delete listing" className="p-1.5 border hover:opacity-70" style={{ borderColor: 'var(--danger)' }}>
                      <Trash2 size={13} style={{ color: 'var(--danger)' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete listing"
        message={`Permanently delete "${deleteTarget?.title?.slice(0, 50)}..."? This cannot be undone.`}
        confirmLabel="DELETE"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
          <div className="w-full max-w-md mx-4 paper-texture border-2 border-foreground ink-shadow">
            <div className="flex items-center justify-between p-4 border-b-2 border-foreground">
              <h3 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>REJECT LISTING</h3>
              <button onClick={() => setRejectTarget(null)} className="p-1 hover:opacity-70"><ClipboardX size={16} /></button>
            </div>
            <div className="p-4 flex flex-col gap-3">
              <p className="text-sm">{rejectTarget.title}</p>
              {rejectTarget.sellerNotes && (
                <div className="p-2.5 border text-xs" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                  <p className="font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>SELLER'S NOTE</p>
                  {rejectTarget.sellerNotes}
                </div>
              )}
              <div>
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  REASON FOR REJECTION <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  Shown to the seller so they know exactly what to fix before resubmitting.
                </p>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="input-ledger w-full px-3 py-2.5 text-sm resize-none"
                  placeholder="e.g. Product photos don't match the description — please upload accurate images."
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-border">
              <button onClick={() => setRejectTarget(null)} className="btn-secondary flex-1 px-4 py-2.5 text-sm">CANCEL</button>
              <button onClick={submitRejection} disabled={rejecting} className="btn-danger flex-1 px-4 py-2.5 text-sm disabled:opacity-60">
                {rejecting ? 'REJECTING...' : 'REJECT WITH REMARKS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailTarget && (
        <ListingReviewModal
          listing={detailTarget}
          onClose={() => setDetailTarget(null)}
          onModerate={async (status) => {
            if (status === 'rejected') {
              setDetailTarget(null);
              handleModerate(detailTarget, 'rejected');
              return;
            }
            await handleModerate(detailTarget, status);
            setDetailTarget((prev) => (prev ? { ...prev, adminStatus: status } : prev));
          }}
          onFeature={async () => {
            await handleFeature(detailTarget);
            setDetailTarget((prev) => (prev ? { ...prev, isFeatured: !prev.isFeatured } : prev));
          }}
          onReviewSeller={() => {
            setDetailTarget(null);
            openKycFromListing(detailTarget);
          }}
        />
      )}

      {kycTarget && (
        <KycReviewModal
          user={kycTarget}
          detail={kycDetail}
          loading={kycLoading}
          onClose={() => { setKycTarget(null); setKycDetail(null); }}
          onApprove={() => handleKycApproval('approved')}
          onReject={() => handleKycApproval('rejected')}
        />
      )}
    </div>
  );
}

// Full, buyer-mirroring listing review modal — shows an admin everything a
// buyer would see (images, price, discount, specs) plus admin-only context
// (seller notes, moderation history, and a shortcut into that seller's KYC),
// with moderation actions available right where the admin is looking at it.
function ListingReviewModal({ listing, onClose, onModerate, onFeature, onReviewSeller }) {
  const [activeImage, setActiveImage] = useState(0);
  const [acting, setActing] = useState('');

  const images = [listing.imageUrl, ...(listing.galleryUrls || [])].filter(Boolean);
  const specEntries = Object.entries(listing.specifications || {});

  const moderationColor = (s) =>
    s === 'approved' ? 'var(--secondary)' : s === 'rejected' ? 'var(--danger)' : 'var(--amber)';
  const moderationBg = (s) =>
    s === 'approved' ? 'var(--success-bg)' : s === 'rejected' ? 'var(--danger-bg)' : 'var(--amber-bg)';

  const runAction = async (key, fn) => {
    setActing(key);
    try {
      await fn();
    } finally {
      setActing('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="w-full max-w-3xl mx-4 paper-texture border-2 border-foreground ink-shadow max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b-2 border-foreground sticky top-0 z-10" style={{ backgroundColor: 'var(--card)' }}>
          <div className="flex items-center gap-2">
            <Package size={16} style={{ color: 'var(--primary)' }} />
            <h3 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              LISTING REVIEW
            </h3>
            <span
              className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
              style={{
                fontFamily: 'var(--font-heading)',
                borderColor: moderationColor(listing.adminStatus),
                color: moderationColor(listing.adminStatus),
                backgroundColor: moderationBg(listing.adminStatus),
              }}
            >
              {listing.adminStatus?.toUpperCase()}
            </span>
          </div>
          <button onClick={onClose} className="p-1 hover:opacity-70 transition-opacity"><ClipboardX size={16} /></button>
        </div>

        <div className="p-4 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ── Gallery — exactly what a buyer would see ─────────── */}
          <div className="lg:col-span-2">
            <div
              className="relative w-full border-2 border-foreground overflow-hidden"
              style={{ height: '260px', backgroundColor: 'var(--background)' }}
            >
              {listing.isFeatured && (
                <div
                  className="absolute top-0 left-0 z-10 flex items-center gap-1 px-2 py-1 text-xs font-heading tracking-stamp"
                  style={{ backgroundColor: 'var(--amber)', color: 'white', fontFamily: 'var(--font-heading)' }}
                >
                  <Star size={10} fill="white" /> FEATURED
                </div>
              )}
              {images.length > 0 ? (
                <img src={images[activeImage]} alt={listing.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                  <ImageOff size={28} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs font-heading tracking-stamp" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                    NO IMAGE PROVIDED
                  </span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {images.map((src, i) => (
                  <button
                    key={`thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className="flex-shrink-0 border-2 overflow-hidden"
                    style={{ width: '52px', height: '52px', borderColor: i === activeImage ? 'var(--primary)' : 'var(--border)' }}
                  >
                    <img src={src} alt={`${listing.title} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Seller card + KYC shortcut */}
            <div className="mt-4 p-3 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
              <p className="text-xs font-heading tracking-stamp mb-1.5" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                SOLD BY
              </p>
              <p className="text-sm font-medium">{listing.sellerCompany}</p>
              <div className="flex items-center gap-1 mt-0.5 mb-2.5">
                <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{listing.sellerCity}</p>
              </div>
              <button
                onClick={onReviewSeller}
                className="btn-secondary w-full px-3 py-2 text-xs flex items-center justify-center gap-1.5"
              >
                <FileText size={12} /> REVIEW SELLER'S KYC (GSTIN / PAN / BANK)
              </button>
            </div>

            <div className="mt-3 flex items-center justify-between text-xs px-1" style={{ color: 'var(--muted-foreground)' }}>
              <span className="flex items-center gap-1"><Eye size={11} /> {listing.viewsCount?.toLocaleString('en-IN') ?? 0} views</span>
              <span>Listed {listing.createdAt ? new Date(listing.createdAt).toLocaleDateString('en-IN') : '—'}</span>
            </div>
          </div>

          {/* ── Details — everything a buyer sees, plus admin context ── */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span
                className="text-xs font-heading tracking-stamp px-2 py-1 border"
                style={{ borderColor: 'var(--primary)', color: 'var(--primary)', fontFamily: 'var(--font-heading)' }}
              >
                {listing.category?.toUpperCase()}
              </span>
              <StatusBadge status={listing.status} size="sm" />
            </div>

            <h2 className="font-heading text-xl tracking-stamp leading-tight mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {listing.title}
            </h2>

            <div className="flex flex-wrap gap-2 mb-4">
              {listing.brand && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs border" style={{ borderColor: 'var(--border)' }}>
                  <Tag size={11} /> {listing.brand}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs border" style={{ borderColor: 'var(--border)' }}>
                <BadgeCheck size={11} /> {listing.condition}
              </span>
              {listing.warranty && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs border" style={{ borderColor: 'var(--border)' }}>
                  <ShieldCheck size={11} /> {listing.warranty}
                </span>
              )}
            </div>

            {/* Price / Market value / Discount / Qty / MOQ — the numbers that matter most for approval */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
              <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>PRICE</p>
                  {listing.discountPercent > 0 && (
                    <span
                      className="text-xs font-heading tracking-stamp px-1 py-0.5"
                      style={{ backgroundColor: 'var(--secondary)', color: 'white', fontFamily: 'var(--font-heading)', fontSize: '9px' }}
                    >
                      {listing.discountPercent}% OFF
                    </span>
                  )}
                </div>
                <p className="text-base font-heading" style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatINR(listing.price)}
                </p>
                {listing.marketValue > listing.price && (
                  <p className="text-xs line-through" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                    {formatINR(listing.marketValue)}
                  </p>
                )}
              </div>
              <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>QTY AVAILABLE</p>
                <p className="text-base font-heading" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                  {listing.quantity?.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>MIN. ORDER QTY</p>
                <p className="text-base font-heading" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                  {listing.moq?.toLocaleString('en-IN') ?? 1}
                </p>
              </div>
              <div className="p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>HSN CODE</p>
                <p className="text-base font-heading" style={{ fontFamily: 'var(--font-mono)' }}>
                  {listing.hsnCode || '—'}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-1.5">
                <ClipboardCheck size={13} style={{ color: 'var(--primary)' }} />
                <h4 className="font-heading text-sm tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>PRODUCT DESCRIPTION</h4>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--foreground)' }}>
                {listing.description}
              </p>
            </div>

            {/* Specifications */}
            {specEntries.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <Boxes size={13} style={{ color: 'var(--primary)' }} />
                  <h4 className="font-heading text-sm tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>SPECIFICATIONS</h4>
                </div>
                <div className="border" style={{ borderColor: 'var(--border)' }}>
                  {specEntries.map(([key, value], i) => (
                    <div
                      key={`spec-${key}`}
                      className="grid grid-cols-2 text-sm"
                      style={{ backgroundColor: i % 2 === 0 ? 'var(--card)' : 'transparent', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}
                    >
                      <div className="px-3 py-2 font-medium" style={{ borderRight: '1px solid var(--border)' }}>{key}</div>
                      <div className="px-3 py-2" style={{ color: 'var(--muted-foreground)' }}>{String(value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {listing.sellerNotes && (
              <div className="p-2.5 border mb-3" style={{ borderColor: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.06)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--primary)' }}>SELLER'S NOTE TO ADMIN</p>
                <p className="text-sm">{listing.sellerNotes}</p>
              </div>
            )}
            {listing.adminNotes && (
              <div className="p-2.5 border mb-3" style={{ borderColor: 'var(--danger)', backgroundColor: 'var(--danger-bg)' }}>
                <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--danger)' }}>PREVIOUS ADMIN REMARKS</p>
                <p className="text-sm">{listing.adminNotes}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Actions — decide right here, no need to leave the review ── */}
        <div className="flex flex-wrap items-center gap-2 p-4 border-t-2 border-foreground sticky bottom-0" style={{ backgroundColor: 'var(--card)' }}>
          <button
            onClick={() => runAction('approved', () => onModerate('approved'))}
            disabled={!!acting}
            className="btn-primary px-4 py-2.5 text-xs flex items-center gap-1.5 disabled:opacity-60"
          >
            <ClipboardCheck size={13} /> {acting === 'approved' ? 'APPROVING...' : 'APPROVE'}
          </button>
          <button
            onClick={() => runAction('pending', () => onModerate('pending'))}
            disabled={!!acting}
            className="btn-secondary px-4 py-2.5 text-xs flex items-center gap-1.5 disabled:opacity-60"
          >
            <Clock3 size={13} /> {acting === 'pending' ? 'UPDATING...' : 'MARK PENDING'}
          </button>
          <button
            onClick={() => runAction('rejected', () => onModerate('rejected'))}
            disabled={!!acting}
            className="btn-danger px-4 py-2.5 text-xs flex items-center gap-1.5 disabled:opacity-60"
          >
            <ClipboardX size={13} /> REJECT...
          </button>
          <button
            onClick={() => runAction('feature', onFeature)}
            disabled={!!acting}
            className="px-4 py-2.5 text-xs border flex items-center gap-1.5 hover:opacity-70 disabled:opacity-60 ml-auto"
            style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}
          >
            {listing.isFeatured ? <StarOff size={13} /> : <Star size={13} />}
            {listing.isFeatured ? 'REMOVE FEATURED' : 'FEATURE LISTING'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Orders Tab ─────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [kycTarget, setKycTarget] = useState(null);
  const [kycDetail, setKycDetail] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      adminApi
        .orders({ status: statusFilter, search })
        .then((data) => setOrders(data.orders || []))
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [statusFilter, search]);

  const openKycForOrder = async (order) => {
    setKycTarget({ id: order.sellerId, name: order.sellerCompany });
    setKycDetail(null);
    setKycLoading(true);
    try {
      const data = await adminApi.userDetail(order.sellerId);
      setKycDetail(data.user);
    } catch (err) {
      toast.error(err.message);
      setKycTarget(null);
    } finally {
      setKycLoading(false);
    }
  };

  // Orders worth chasing up: payment is in, but the shipment hasn't moved
  // yet or is still on the road.
  const followUpCount = orders.filter((o) => o.paymentStatus === 'paid' && o.status !== 'delivered').length;

  const handleKycDecision = async (approvalStatus) => {
    if (!kycTarget) return;
    try {
      await adminApi.setUserApproval(kycTarget.id, approvalStatus);
      toast.success(`${kycTarget.name} ${approvalStatus === 'approved' ? 'approved.' : 'rejected.'}`);
      setKycTarget(null);
      setKycDetail(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <Toolbar search={search} onSearch={setSearch}>
        <div className="flex gap-2 flex-wrap">
          {['', 'pending_confirmation', 'confirmed', 'in_transit', 'delivered'].map((s) => (
            <button
              key={`order-status-${s || 'all'}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-xs ${statusFilter === s ? 'tab-active' : 'tab-inactive'}`}
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {s ? s.replace('_', ' ').toUpperCase() : 'ALL'}
            </button>
          ))}
        </div>
      </Toolbar>

      {!loading && followUpCount > 0 && (
        <div
          className="flex items-center gap-1.5 px-2.5 py-1.5 border text-xs mb-3 inline-flex"
          style={{ backgroundColor: 'var(--amber-bg)', borderColor: 'var(--amber)', color: 'var(--amber)' }}
        >
          <Clock3 size={12} />
          <span className="font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
            {followUpCount} PAID ORDER{followUpCount === 1 ? '' : 'S'} STILL MOVING THROUGH FULFILLMENT
          </span>
        </div>
      )}

      {loading ? (
        <LoadingBlock label="LOADING ORDERS..." />
      ) : orders.length === 0 ? (
        <EmptyBlock icon={ShoppingBag} message="No orders match this filter." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--foreground)' }}>
                {['LISTING', 'BUYER', 'SHIP TO', 'SELLER', 'VALUE', 'COMMISSION', 'PAYMENT', 'SHIPMENT STATUS', 'TRACKING'].map((c) => (
                  <th key={c} className="px-3 py-2.5 text-left text-xs font-heading tracking-stamp whitespace-nowrap" style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={o.id} className="border-b border-border" style={{ backgroundColor: i % 2 === 0 ? 'var(--card)' : 'transparent' }}>
                  <td className="px-3 py-2.5 max-w-[220px]"><p className="text-sm truncate">{o.listingTitle}</p></td>
                  <td className="px-3 py-2.5 text-xs">{o.buyerName}{o.buyerCompany ? ` · ${o.buyerCompany}` : ''}</td>
                  <td className="px-3 py-2.5 text-xs max-w-[190px]">
                    {o.shipping?.name ? (
                      <div
                        className="cursor-help"
                        title={`${o.shipping.name} · ${o.shipping.mobile}${o.shipping.altMobile ? ' / ' + o.shipping.altMobile : ''}\n${o.shipping.addressLine1}${o.shipping.addressLine2 ? ', ' + o.shipping.addressLine2 : ''}, ${o.shipping.city}, ${o.shipping.state} ${o.shipping.pincode}`}
                      >
                        <p className="font-medium truncate">{o.shipping.name}</p>
                        <p className="font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>{o.shipping.mobile}</p>
                        <p className="truncate" style={{ color: 'var(--muted-foreground)' }}>
                          {o.shipping.addressLine1}{o.shipping.addressLine2 ? `, ${o.shipping.addressLine2}` : ''}
                        </p>
                        <p style={{ color: 'var(--muted-foreground)' }}>{o.shipping.city}, {o.shipping.state} {o.shipping.pincode}</p>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <button onClick={() => openKycForOrder(o)} className="text-left hover:underline" title="Review this seller's KYC">
                      {o.sellerCompany}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(o.totalValue)}</td>
                  <td className="px-3 py-2.5 text-xs font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--secondary)' }}>
                    {formatINR(o.commissionAmount)} <span style={{ color: 'var(--muted-foreground)' }}>({o.commissionRate}%)</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className="text-xs font-heading tracking-stamp px-2 py-0.5 border inline-block"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        borderColor: o.paymentStatus === 'paid' ? 'var(--secondary)' : o.paymentStatus === 'failed' ? 'var(--danger)' : 'var(--amber)',
                        color: o.paymentStatus === 'paid' ? 'var(--secondary)' : o.paymentStatus === 'failed' ? 'var(--danger)' : 'var(--amber)',
                        backgroundColor: o.paymentStatus === 'paid' ? 'var(--success-bg)' : o.paymentStatus === 'failed' ? 'var(--danger-bg)' : 'var(--amber-bg)',
                      }}
                    >
                      {o.paymentStatus?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={o.status} size="sm" /></td>
                  <td className="px-3 py-2.5 text-xs max-w-[180px]">
                    {o.trackingNumber ? (
                      <div className="flex items-start gap-1">
                        <Truck size={12} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--muted-foreground)' }} />
                        <div>
                          <p className="truncate">{o.courierName}</p>
                          <p className="font-mono-data truncate" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted-foreground)' }}>{o.trackingNumber}</p>
                        </div>
                      </div>
                    ) : o.paymentStatus === 'paid' ? (
                      <span className="text-xs" style={{ color: 'var(--amber)' }}>Not shipped yet</span>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {kycTarget && (
        <KycReviewModal
          user={kycTarget}
          detail={kycDetail}
          loading={kycLoading}
          onClose={() => { setKycTarget(null); setKycDetail(null); }}
          onApprove={() => handleKycDecision('approved')}
          onReject={() => handleKycDecision('rejected')}
        />
      )}
    </div>
  );
}

function LoadingBlock({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="inline-block w-7 h-7 border-2 border-t-transparent rounded-full animate-spin mb-3" style={{ borderColor: 'var(--primary)' }} />
      <p className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>{label}</p>
    </div>
  );
}

function EmptyBlock({ icon: Icon, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed" style={{ borderColor: 'var(--border)' }}>
      <Icon size={32} className="mb-3" style={{ color: 'var(--muted-foreground)' }} />
      <p className="text-sm text-center max-w-sm" style={{ color: 'var(--muted-foreground)' }}>{message}</p>
    </div>
  );
}

// ─── Main Admin Dashboard ───────────────────────────────────
export default function AdminDashboardClient() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = getStoredToken();
    const storedUser = getStoredUser();
    if (!token || storedUser?.role !== 'admin') {
      navigate('/admin/login', { replace: true });
      return;
    }
    setAdmin(storedUser);

    adminApi
      .stats()
      .then((data) => {
        setStats(data.stats);
        setAuthChecked(true);
      })
      .catch((err) => {
        // Token invalid / MFA not verified / expired — bounce to admin login.
        clearAuth();
        navigate('/admin/login', { replace: true });
      });
  }, [navigate]);

  const handleLogout = () => {
    clearAuth();
    navigate('/admin/login');
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3" style={{ borderColor: 'var(--primary)' }} />
          <p className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>VERIFYING ADMIN SESSION...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'OVERVIEW', icon: TrendingUp },
    { key: 'users', label: 'USERS', icon: Users },
    { key: 'listings', label: 'LISTINGS', icon: Package },
    { key: 'orders', label: 'ORDERS', icon: ShoppingBag },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      <header className="sticky top-0 z-40 border-b-2 border-foreground" style={{ backgroundColor: 'var(--foreground)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <div className="flex items-center gap-1.5">
              <ShieldAlert size={15} style={{ color: 'var(--primary)' }} />
              <span className="font-heading text-sm tracking-stamp" style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}>
                ADMIN CONTROL PANEL
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono-data hidden sm:inline" style={{ color: 'rgba(237,232,220,0.6)', fontFamily: 'var(--font-mono)' }}>
              {admin?.name}
            </span>
            <button onClick={handleLogout} className="btn-danger flex items-center gap-1.5 px-3 py-1.5 text-xs">
              <LogOut size={12} /> LOG OUT
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 lg:px-8 py-6">
        {/* KPI Overview — always visible summary strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <AdminKpi label="TOTAL USERS" value={stats.totalUsers} icon={Users} />
            <AdminKpi label="PENDING APPROVAL" value={stats.pendingApprovals} icon={UserCheck} accent="var(--amber)" />
            <AdminKpi label="SUSPENDED" value={stats.suspendedUsers} icon={Ban} accent="var(--danger)" />
            <AdminKpi label="LISTINGS" value={stats.totalListings} icon={Package} />
            <AdminKpi label="PENDING REVIEW" value={stats.pendingListings} icon={Clock3} accent="var(--amber)" />
            <AdminKpi label="GMV" value={formatINR(stats.gmv)} icon={TrendingUp} accent="var(--secondary)" />
            <AdminKpi label="PLATFORM REVENUE" value={formatINR(stats.platformRevenue)} icon={Wallet} accent="var(--secondary)" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs whitespace-nowrap ${activeTab === tab.key ? 'tab-active' : 'tab-inactive'}`}
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              <tab.icon size={13} />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="paper-texture border-2 border-foreground p-4">
              <h3 className="font-heading text-sm tracking-stamp mb-3" style={{ fontFamily: 'var(--font-heading)' }}>USER BASE</h3>
              <div className="flex flex-col gap-2 text-sm">
                <Row label="Sellers" value={stats.sellers} />
                <Row label="Buyers" value={stats.buyers} />
                <Row label="Awaiting approval" value={stats.pendingApprovals} warn={stats.pendingApprovals > 0} />
                <Row label="Suspended accounts" value={stats.suspendedUsers} warn={stats.suspendedUsers > 0} />
              </div>
            </div>
            <div className="paper-texture border-2 border-foreground p-4">
              <h3 className="font-heading text-sm tracking-stamp mb-3" style={{ fontFamily: 'var(--font-heading)' }}>LISTING MODERATION</h3>
              <div className="flex flex-col gap-2 text-sm">
                <Row label="Total listings" value={stats.totalListings} />
                <Row label="Pending review" value={stats.pendingListings} warn={stats.pendingListings > 0} />
                <Row label="Rejected" value={stats.rejectedListings} />
              </div>
            </div>
            <div className="paper-texture border-2 border-foreground p-4">
              <h3 className="font-heading text-sm tracking-stamp mb-3" style={{ fontFamily: 'var(--font-heading)' }}>TRANSACTIONS</h3>
              <div className="flex flex-col gap-2 text-sm">
                <Row label="Deals closed (delivered)" value={stats.dealsClosed} />
                <Row label="Orders in progress" value={stats.pendingOrders} />
                <Row label="Gross Merchandise Value" value={formatINR(stats.gmv)} />
              </div>
            </div>
            <div className="paper-texture border-2 border-foreground p-4">
              <h3 className="font-heading text-sm tracking-stamp mb-3" style={{ fontFamily: 'var(--font-heading)' }}>PLATFORM REVENUE</h3>
              <div className="flex flex-col gap-2 text-sm">
                <Row label="Commission earned (delivered orders)" value={formatINR(stats.platformRevenue)} highlight />
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Category-tiered take rate, 5–12% per the ClearanceConnect commission model.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'listings' && <ListingsTab />}
        {activeTab === 'orders' && <OrdersTab />}
      </main>

      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function Row({ label, value, warn, highlight }) {
  return (
    <div className="flex items-center justify-between pb-2 border-b border-border last:border-0 last:pb-0">
      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{label}</span>
      <span
        className="text-sm font-mono-data"
        style={{
          fontFamily: 'var(--font-mono)',
          color: warn ? 'var(--amber)' : highlight ? 'var(--secondary)' : 'var(--foreground)',
          fontWeight: highlight ? 600 : 400,
        }}
      >
        {value}
      </span>
    </div>
  );
}
